import React, { useEffect, useState } from 'react';
import { Select } from 'antd';
import { IProfile } from 'entities/Permission/Permission.models';
import { permissionTransport } from 'entities/Permission/Permission.transport';

interface IComponentProps {
  value?: string;
  onChange?: (profileId: string | undefined, profile?: IProfile) => void;
  // Fires once the catalog loads and a `value` is already set (edit page prefill) so the parent can
  // render the preview without the user re-selecting.
  onResolve?: (profile?: IProfile) => void;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
}

// ponytail: profile catalog is a tiny hardcoded BE list (Marketing / Sales Operations), so a one-shot
// fetch into a plain searchable Select is enough — no redux slice, no pagination.
export const ProfileSelector: React.FC<IComponentProps> = ({
  value,
  onChange,
  onResolve,
  placeholder = 'Select position',
  disabled,
  allowClear = true,
}) => {
  const [profiles, setProfiles] = useState<IProfile[]>([]);

  useEffect(() => {
    let active = true;

    permissionTransport
      .getProfiles()
      .then((collection) => {
        if (active) {
          setProfiles(collection.data);
        }
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, []);

  // The catalog and the field's `value` (Form.Item-injected, e.g. from an async-loaded userModel)
  // can each arrive after the other — re-resolve whenever either one changes, not just on mount.
  useEffect(() => {
    if (value && profiles.length) {
      onResolve?.(profiles.find((p) => p.id === value));
    }
  }, [value, profiles]);

  const handleChange = (val?: string) => {
    onChange?.(
      val,
      profiles.find((p) => p.id === val)
    );
  };

  return (
    <Select
      allowClear={allowClear}
      showSearch
      className="width-full"
      placeholder={placeholder}
      disabled={disabled}
      value={value}
      optionFilterProp="children"
      filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())}
      onChange={handleChange}
      getPopupContainer={(triggerNode) => triggerNode as HTMLElement}
    >
      {profiles.map((profile) => (
        <Select.Option key={profile.id} value={profile.id}>
          {profile.name}
        </Select.Option>
      ))}
    </Select>
  );
};
